import { createBrowserRouter, Navigate } from 'react-router-dom'
import ProtectedRoute from './ProtectedRoute'
import RoleGuard from './RoleGuard'
import { ROLES } from '@config/roles'

// Layouts
import RoleBasedLayout from '@/components/layout/RoleBasedLayout'

// Auth Pages
import Login from '@/pages/Login'
import Activation from '@/pages/public/Activation/Activation'
import ForgotPassword from '@/pages/ForgotPassword/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword/ResetPassword'

// Role-Based Dashboard
import { RoleBasedDashboard } from '@/components/features/dashboard'

// Super Admin Pages
import SuperAdminCompanies from '@/pages/super-admin/Companies'
import SuperAdminFinance from '@/pages/super-admin/Finance'
import SuperAdminUsers from '@/pages/super-admin/Users'
import SuperAdminItems from '@/pages/super-admin/Items'
import SuperAdminAuditLogs from '@/pages/super-admin/AuditLogs'
import Packages from '@/pages/super-admin/Packages'
import Certificates from '@/pages/super-admin/Certificates'
import Roles from '@/pages/super-admin/Roles'
import Features from '@/pages/super-admin/Features'

// Shared Pages
import SharedReports from '@/pages/shared/Reports'
import CompanyReports from '@/pages/company/Reports'
import SharedSettings from '@/pages/shared/Settings'
import AccessDenied from '@/pages/shared/AccessDenied'
import MyTasks from '@/pages/shared/MyTasks'
import Notifications from '@/pages/shared/Notifications/Notifications'

// Profile
import Profile from '@/pages/Profile'

// Company Pages
import NewProject from '@/pages/company/NewProject'
import Projects from '@/pages/company/Projects'
import ProjectDetail from '@/pages/company/ProjectDetail'
import EditProject from '@/pages/company/EditProject'
import Workflows, {
  WorkflowCreate,
  WorkflowEdit,
  WorkflowView,
} from '@/pages/company/Workflows'
import TeamMembers from '@/pages/company/TeamMembers'
import CompanyRoles from '@/pages/company/Roles'
import TaskDetail from '@/pages/company/TaskDetail'
import CompanySettings from '@/pages/company/Settings'
import Resources from '@/pages/company/Resources'
import ComplianceDashboard from '@/pages/company/ComplianceDashboard'

/**
 * Application Router Configuration
 *
 * Route Structure:
 * - /login - Public authentication page
 * - /activate - Account activation page
 *
 * Role-Based Routing:
 * - /dashboard - Dynamically loads dashboard based on user role
 * - Routes protected by RoleGuard based on required permissions
 *
 * Access Control:
 * - SUPER_ADMIN: Full access to all routes
 * - COMPANY_ADMIN: Company-level management
 * - PROJECT_MANAGER: Project and task management
 * - MANAGEMENT/EMPLOYEE: Limited access based on permissions
 */
export const router = createBrowserRouter([
  // Public Routes
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/reset-password/:token',
    element: <ResetPassword />,
  },
  {
    path: '/activate',
    element: <Activation />,
  },

  // Main Application Routes - Role-based navigation and content
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <RoleBasedLayout />
      </ProtectedRoute>
    ),
    children: [
      // Redirect root to dashboard
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },

      // Access Denied page
      {
        path: 'access-denied',
        element: <AccessDenied />,
      },

      // Role-Based Dashboard - Shows appropriate dashboard based on user role
      {
        path: 'dashboard',
        element: <RoleBasedDashboard />,
      },

      // ============================================
      // Super Admin Only Routes
      // ============================================
      {
        path: 'companies',
        element: (
          <RoleGuard allowedRoles={[ROLES.SUPER_ADMIN]}>
            <SuperAdminCompanies />
          </RoleGuard>
        ),
      },
      {
        path: 'packages',
        element: (
          <RoleGuard allowedRoles={[ROLES.SUPER_ADMIN]}>
            <Packages />
          </RoleGuard>
        ),
      },
      {
        path: 'features',
        element: (
          <RoleGuard allowedRoles={[ROLES.SUPER_ADMIN]}>
            <Features />
          </RoleGuard>
        ),
      },
      {
        path: 'finance',
        element: (
          <RoleGuard allowedRoles={[ROLES.SUPER_ADMIN]}>
            <SuperAdminFinance />
          </RoleGuard>
        ),
      },
      {
        path: 'items',
        element: (
          <RoleGuard allowedRoles={[ROLES.SUPER_ADMIN]}>
            <SuperAdminItems />
          </RoleGuard>
        ),
      },
      {
        path: 'roles',
        element: (
          <RoleGuard allowedRoles={[ROLES.SUPER_ADMIN]}>
            <Roles />
          </RoleGuard>
        ),
      },

      // ============================================
      // Super Admin Only - Users Management
      // ============================================
      {
        path: 'users',
        element: (
          <RoleGuard allowedRoles={[ROLES.SUPER_ADMIN]}>
            <SuperAdminUsers />
          </RoleGuard>
        ),
      },

      // ============================================
      // Company Admin - Team Members Management
      // ============================================
      {
        path: 'team-members',
        element: (
          <RoleGuard allowedRoles={[ROLES.COMPANY_ADMIN]}>
            <TeamMembers />
          </RoleGuard>
        ),
      },

      // ============================================
      // Company Admin - Role Management
      // ============================================
      {
        path: 'company-roles',
        element: (
          <RoleGuard allowedRoles={[ROLES.COMPANY_ADMIN]}>
            <CompanyRoles />
          </RoleGuard>
        ),
      },

      // ============================================
      // Admin Routes (Super Admin + Company Admin)
      // ============================================
      {
        path: 'certificates',
        element: (
          <RoleGuard allowedRoles={[ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN]}>
            <Certificates />
          </RoleGuard>
        ),
      },
      {
        path: 'audit-logs',
        element: (
          <RoleGuard allowedRoles={[ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN]}>
            <SuperAdminAuditLogs />
          </RoleGuard>
        ),
      },
      // ============================================
      // Company User Routes
      // ============================================
      {
        path: 'projects',
        element: (
          <RoleGuard allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.PROJECT_MANAGER, ROLES.MANAGEMENT]}>
            <Projects />
          </RoleGuard>
        ),
      },
      {
        path: 'projects/new',
        element: (
          <RoleGuard allowedRoles={[ROLES.COMPANY_ADMIN]}>
            <NewProject />
          </RoleGuard>
        ),
      },
      {
        path: 'projects/:id',
        element: (
          <RoleGuard allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.COMPLIANCE_MANAGER, ROLES.PROJECT_MANAGER, ROLES.MANAGEMENT]}>
            <ProjectDetail />
          </RoleGuard>
        ),
      },
      {
        path: 'projects/:id/edit',
        element: (
          <RoleGuard allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.PROJECT_MANAGER]}>
            <EditProject />
          </RoleGuard>
        ),
      },

      // ============================================
      // Workflow Routes (Company Admin Only)
      // ============================================
      {
        path: 'workflows',
        element: (
          <RoleGuard allowedRoles={[ROLES.COMPANY_ADMIN]}>
            <Workflows />
          </RoleGuard>
        ),
      },
      {
        path: 'workflows/new',
        element: (
          <RoleGuard allowedRoles={[ROLES.COMPANY_ADMIN]}>
            <WorkflowCreate />
          </RoleGuard>
        ),
      },
      {
        path: 'workflows/:id',
        element: (
          <RoleGuard allowedRoles={[ROLES.COMPANY_ADMIN]}>
            <WorkflowView />
          </RoleGuard>
        ),
      },
      {
        path: 'workflows/:id/edit',
        element: (
          <RoleGuard allowedRoles={[ROLES.COMPANY_ADMIN]}>
            <WorkflowEdit />
          </RoleGuard>
        ),
      },

      // ============================================
      // Company Resources (All company users)
      // ============================================
      {
        path: 'resources',
        element: (
          <RoleGuard allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.PROJECT_MANAGER, ROLES.MANAGEMENT, ROLES.EMPLOYEE]}>
            <Resources />
          </RoleGuard>
        ),
      },

      // ============================================
      // Compliance Dashboard (Management level view)
      // ============================================
      {
        path: 'compliance-dashboard',
        element: (
          <RoleGuard allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.COMPLIANCE_MANAGER, ROLES.MANAGEMENT, ROLES.EXECUTIVE]}>
            <ComplianceDashboard />
          </RoleGuard>
        ),
      },

      // ============================================
      // Shared Routes (All authenticated users)
      // ============================================
      {
        path: 'my-tasks',
        element: <MyTasks />,
      },
      {
        path: 'tasks/:taskId',
        element: <TaskDetail />,
      },
      {
        // Company-level reports: visible to admin, compliance-manager (maps to COMPANY_ADMIN),
        // and management/executive (both map to ROLES.MANAGEMENT in RoleGuard).
        // All other roles land on /my-reports (personal reports).
        path: 'reports',
        element: (
          <RoleGuard
            allowedRoles={[ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGEMENT]}
            redirectTo="/my-reports"
          >
            <CompanyReports />
          </RoleGuard>
        ),
      },
      {
        // Personal reports for employees and project managers
        path: 'my-reports',
        element: <SharedReports />,
      },
      {
        path: 'settings',
        element: <SharedSettings />,
      },
      {
        path: 'company-settings',
        element: (
          <RoleGuard allowedRoles={[ROLES.COMPANY_ADMIN]}>
            <CompanySettings />
          </RoleGuard>
        ),
      },
      {
        path: 'profile',
        element: <Profile />,
      },
      {
        path: 'notifications',
        element: <Notifications />,
      },
    ],
  },

  // Legacy Super Admin Routes - Redirect to new unified routes
  {
    path: '/super-admin/*',
    element: <Navigate to="/dashboard" replace />,
  },

  // Catch-all redirect to dashboard
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />,
  },
])

export default router
